import type { ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Estados vazio e de erro do cardápio, em um lugar só.
 *
 * Antes cada tela inventava o seu — ícone, texto e espaçamento diferentes para
 * dizer a mesma coisa —, e a maioria simplesmente não tratava falha de
 * carregamento: com a API fora do ar o cliente ficava olhando um spinner que
 * nunca terminava. Num totem isso é pior, porque não tem ninguém do lado para
 * explicar.
 *
 * `tamanho="grande"` é para os aparelhos da loja (totem/tablet), onde a
 * tipografia do cardápio inteiro é maior e o alvo de toque precisa ser generoso.
 */
type Tamanho = 'normal' | 'grande';

interface EstadoBaseProps {
  icone?: ReactNode;
  titulo: string;
  descricao?: string;
  acao?: ReactNode;
  tamanho?: Tamanho;
}

function EstadoBase({ icone, titulo, descricao, acao, tamanho = 'normal' }: EstadoBaseProps): JSX.Element {
  const grande = tamanho === 'grande';

  return (
    <Stack
      spacing={grande ? 2 : 1.5}
      sx={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', p: grande ? 4 : 3, flexGrow: 1 }}
    >
      {icone && (
        <Box sx={{ color: 'text.disabled', '& > svg': { fontSize: grande ? 72 : 48 } }}>{icone}</Box>
      )}

      <Typography variant={grande ? 'h5' : 'subtitle1'} sx={{ fontWeight: 600 }}>
        {titulo}
      </Typography>

      {descricao && (
        <Typography variant={grande ? 'body1' : 'body2'} color="text.secondary" sx={{ maxWidth: 420 }}>
          {descricao}
        </Typography>
      )}

      {acao}
    </Stack>
  );
}

/** Lista sem conteúdo — carrinho vazio, catálogo sem produtos, sem pedidos. */
export function EstadoVazio(props: EstadoBaseProps): JSX.Element {
  return <EstadoBase {...props} />;
}

/**
 * Falha ao carregar. O texto padrão evita jargão e diz o que fazer; quem passar
 * `aoTentarNovamente` ganha o botão de recarregar.
 */
export function EstadoErro({
  titulo = 'Não foi possível carregar',
  descricao = 'Verifique sua conexão e tente de novo. Se continuar, chame um atendente.',
  aoTentarNovamente,
  tamanho = 'normal',
}: {
  titulo?: string;
  descricao?: string;
  aoTentarNovamente?: () => void;
  tamanho?: Tamanho;
}): JSX.Element {
  return (
    <EstadoBase
      icone={<ErrorOutlineIcon />}
      titulo={titulo}
      descricao={descricao}
      tamanho={tamanho}
      acao={
        aoTentarNovamente && (
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={aoTentarNovamente}
            size={tamanho === 'grande' ? 'large' : 'medium'}
          >
            Tentar de novo
          </Button>
        )
      }
    />
  );
}

